import { useRef } from 'react';
import contactMeFormStyle from './ContactMeForm.module.scss';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import ITextInput from '../elements/iTextInput/ITextInput';
import IButton from '../elements/iButton/IButton';
import { ISendEmailPayload } from '../../interfaces/interfaces';
import SendIcon from '@mui/icons-material/Send';

const MESSAGE_MAX_LENGTH = 300;

// Set REACT_APP_RECAPTCHA_CLIENT_KEY in .env (and in the Vercel project
// settings). Without it the widget cannot render, so we skip it rather than
// let react-google-recaptcha throw "Missing required parameters: sitekey".
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_CLIENT_KEY;

interface IProps {
    sumbitFormHandler: (payload: ISendEmailPayload) => void;
}

const ContactMeForm = (props: IProps) => {
    const { sumbitFormHandler } = props;
    const { t } = useTranslation();
    const captchaRef = useRef<any>(null);

    const validationSchema = yup.object().shape({
        senderName: yup.string().required(t('contactMeForm.requiredField')),
        senderEmail: yup.string().email(t('contactMeForm.invalidEmail')).required(t('contactMeForm.requiredField')),
        message: yup.string().required(t('contactMeForm.requiredField')),
    });

    const formik = useFormik({
        initialValues: {
            senderName: '',
            senderEmail: '',
            message: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            const captchaV2Token: string = captchaRef.current?.getValue() || '';

            captchaRef.current?.reset();
            formik.resetForm();

            sumbitFormHandler({ ...values, captchaV2Token });
        },
    });

    const remaining = MESSAGE_MAX_LENGTH - formik.values.message.length;

    return (
        <form className={contactMeFormStyle.form} onSubmit={formik.handleSubmit} noValidate>
            <div className={contactMeFormStyle.row}>
                <ITextInput
                    id='senderName'
                    name='senderName'
                    label={t('contactMeForm.name')}
                    placeholder={t('contactMeForm.namePlaceholder')}
                    value={formik.values.senderName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.senderName && Boolean(formik.errors.senderName)}
                    helperText={formik.touched.senderName && formik.errors.senderName}
                    maxLength={30}
                />
                <ITextInput
                    id='senderEmail'
                    name='senderEmail'
                    type='email'
                    label={t('contactMeForm.email')}
                    placeholder={t('contactMeForm.emailPlaceholder')}
                    value={formik.values.senderEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.senderEmail && Boolean(formik.errors.senderEmail)}
                    helperText={formik.touched.senderEmail && formik.errors.senderEmail}
                    maxLength={50}
                />
            </div>

            <ITextInput
                id='message'
                name='message'
                label={t('contactMeForm.message')}
                placeholder={t('contactMeForm.messagePlaceholder')}
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.message && Boolean(formik.errors.message)}
                helperText={formik.touched.message && formik.errors.message}
                multiline
                maxLength={MESSAGE_MAX_LENGTH}
            />

            <div className={contactMeFormStyle.counter}>
                <span className={remaining <= 30 ? contactMeFormStyle.counterLow : ''}>
                    {formik.values.message.length}/{MESSAGE_MAX_LENGTH}
                </span>
            </div>

            {RECAPTCHA_SITE_KEY && (
                <div className={contactMeFormStyle.captcha}>
                    <ReCAPTCHA ref={captchaRef} sitekey={RECAPTCHA_SITE_KEY} theme='dark' />
                </div>
            )}

            <IButton
                title={t('contactMeForm.submit')}
                type='submit'
                variant='primary'
                size='lg'
                fullWidth
                icon={<SendIcon />}
                disabled={formik.isSubmitting}
            />
        </form>
    );
};

export default ContactMeForm;
